<script setup>
const props = defineProps({
  name: {
    type: String,
    default: 'name',
  },
  label: {
    type: String,
    required: true,
  },
  placeholder: {
    type: String,
    default: undefined,
  },
  options: {
    type: Array,
    default: () => [],
  },
  optionIdKey: {
    type: String,
    default: 'id',
  },
  optionLabelKey: {
    type: String,
    default: 'name',
  },
  allowEmpty: {
    type: Boolean,
    default: false,
  },
});

const model = defineModel({ type: [String, Number, Array], default: null });

const { value, errorMessage, handleChange, handleBlur } = useField(
  () => props.name,
  null,
  { initialValue: model.value },
);

useSyncModels(model, value);

const rootRef = ref(null);
const { focused } = useFocus(() => rootRef.value?.$el);
const toggleFocused = useToggle(focused);

const [focusedDelay, toggleFocusedDelay] = useToggle(false);
watch(focused, async (focused) => {
  await awaitRAF();
  toggleFocusedDelay(focused);
});

const valueDisplay = computed(() => {
  if (Array.isArray(value.value)) {
    const firstOption = props.options.find((option) =>
      value.value.includes(option[props.optionIdKey]),
    )?.[props.optionLabelKey];

    return value.value.length > 1
      ? `${firstOption} +${value.value.length - 1}`
      : firstOption;
  } else {
    return props.options.find(
      (option) => option[props.optionIdKey] === value.value,
    )?.[props.optionLabelKey];
  }
});

async function handleRootMousedown() {
  if (!focusedDelay.value) return;

  await awaitRAF();
  toggleFocused(false);
}
</script>

<template>
  <UiFormInputBase
    ref="rootRef"
    class="form-select-base"
    tag="span"
    tabindex="0"
    :label="label"
    :is-active="focused"
    :error="errorMessage"
    @blur="handleBlur($event, true)"
    @mousedown="handleRootMousedown()"
  >
    <template #icon>
      <CIcon
        name="arrow-down"
        class="form-select-base__icon form-select-base__icon--arrow"
        :class="{ 'form-select-base__icon--arrow-rotate': focused }"
      />
    </template>

    <span
      class="form-select-base__font form-select-base__font--value"
      :class="{ 'form-select-base__font--value-error': errorMessage }"
    >
      {{ valueDisplay || placeholder }}
    </span>

    <span
      v-if="focused"
      class="form-select-base__dropdown"
      @mousedown.prevent.stop
    >
      <slot
        name="dropdown"
        :value="value"
        :handle-change="handleChange"
        :toggle-focused="toggleFocused"
      />
    </span>
  </UiFormInputBase>
</template>

<style scoped lang="scss">
.form-select-base {
  cursor: pointer;
  position: relative;

  &__font {
    &--value {
      font-size: em(18);
      line-height: em(20, 18);
      color: $color-black;

      @include media-breakpoint-down(sm) {
        font-size: em(16);
        line-height: em(18, 16);
      }
    }

    &--value-error {
      color: $color-red-1;
    }
  }

  &__icon {
    &--arrow {
      align-self: flex-start;
      flex-shrink: 0;
      width: em(19);
      margin-top: em(4.5);

      @include media-breakpoint-down(sm) {
        width: em(14);
        margin-top: em(5);
      }
    }

    &--arrow-rotate {
      transform: rotate(180deg);
    }
  }

  &__dropdown {
    cursor: auto;
    display: block;
    position: absolute;
    z-index: 100;
    top: calc(100% + 1px);
    left: -1px;
    right: -1px;
    background-color: $color-white;
    border: 1px solid $color-gray-1;
    border-top: none;
    padding: em(17);
    max-height: em(426);
    overflow: hidden auto;
    @include custom-scrollbar;
  }
}
</style>
